export type AttendanceSource = "flowboard" | "calendar";

export type AttendanceDraft = {
  id: string;
  appointmentId: string;
  clinicId: string;
  ownerId: string;
  petId: string;
  type: string;
  startedAtIso: string;
  source: AttendanceSource;
  createdByUserId: string;
  ownerName?: string;
  petName?: string;
};

const ATTENDANCE_DRAFT_KEY_PREFIX = "akuna:attendance:draft";
const ATTENDANCE_DRAFT_TTL_MS = 12 * 60 * 60 * 1000;

type PersistedAttendanceDraft = AttendanceDraft & {
  expiresAtIso: string;
};

export const getAttendanceDraftStorageKey = (clinicId: string, createdByUserId: string) =>
  `${ATTENDANCE_DRAFT_KEY_PREFIX}:${clinicId}:${createdByUserId}`;

const canUseSessionStorage = () =>
  typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

export const readAttendanceDraft = (
  clinicId: string,
  createdByUserId: string,
): AttendanceDraft | null => {
  if (!canUseSessionStorage()) return null;

  const key = getAttendanceDraftStorageKey(clinicId, createdByUserId);
  const raw = window.sessionStorage.getItem(key);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PersistedAttendanceDraft>;
    if (
      !parsed ||
      typeof parsed.id !== "string" ||
      typeof parsed.appointmentId !== "string" ||
      typeof parsed.ownerId !== "string" ||
      typeof parsed.petId !== "string" ||
      typeof parsed.clinicId !== "string" ||
      typeof parsed.createdByUserId !== "string" ||
      typeof parsed.type !== "string" ||
      typeof parsed.startedAtIso !== "string" ||
      typeof parsed.source !== "string" ||
      typeof parsed.expiresAtIso !== "string"
    ) {
      window.sessionStorage.removeItem(key);
      return null;
    }

    const expiresAt = new Date(parsed.expiresAtIso);
    if (Number.isNaN(expiresAt.getTime()) || expiresAt.getTime() <= Date.now()) {
      window.sessionStorage.removeItem(key);
      return null;
    }

    return {
      id: parsed.id,
      appointmentId: parsed.appointmentId,
      clinicId: parsed.clinicId,
      ownerId: parsed.ownerId,
      petId: parsed.petId,
      type: parsed.type,
      startedAtIso: parsed.startedAtIso,
      source: parsed.source as AttendanceSource,
      createdByUserId: parsed.createdByUserId,
      ownerName: typeof parsed.ownerName === "string" ? parsed.ownerName : undefined,
      petName: typeof parsed.petName === "string" ? parsed.petName : undefined,
    };
  } catch {
    window.sessionStorage.removeItem(key);
    return null;
  }
};

export const writeAttendanceDraft = (draft: AttendanceDraft) => {
  if (!canUseSessionStorage()) return;

  const key = getAttendanceDraftStorageKey(draft.clinicId, draft.createdByUserId);
  const expiresAtIso = new Date(Date.now() + ATTENDANCE_DRAFT_TTL_MS).toISOString();
  const payload: PersistedAttendanceDraft = {
    ...draft,
    expiresAtIso,
  };
  window.sessionStorage.setItem(key, JSON.stringify(payload));
};

export const clearAttendanceDraft = (clinicId: string, createdByUserId: string) => {
  if (!canUseSessionStorage()) return;
  const key = getAttendanceDraftStorageKey(clinicId, createdByUserId);
  window.sessionStorage.removeItem(key);
};
